/*
 * Sourced from https://v0.dev/chat/menu-navbar-sidebar-w-o-localization-Y0lZuopJCUv
 * See docs/v0.md for more
 */

'use client'

import type React from 'react'
import { useLocale, useTranslations } from 'next-intl'

import { CheckIcon, ChevronRight, PlusIcon } from 'lucide-react'

import { currencySymbol } from '@/lib/currency/picker'
import { cn } from '@/lib/utils'
import { useSession } from '@/hooks/use-session'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarRail,
    SidebarSeparator,
} from '@/components/ui/sidebar'
import { Link } from '@/i18n/navigation'
import { trackNavClickEvent } from '@/services/auth/client/auth-events'

import type { NavbarProps } from './navbar'
import { CurrencyMenuVertical } from './navigation/currency-menu'
import { CountryFlag } from './navigation/locale-menu'
import { activeLocaleOption, type LocaleOption } from './navigation/locale-menu-config'

/**
 * One country in the sidebar's picker — the vertical counterpart of the navbar's
 * `CountryRow`, and the same rule: a country served in one language is a single link,
 * a country served in several is a name with a link per language (ui-react's
 * `Canada EN FR`). The country name stays unlinked in the multi-language case for the
 * reason the navbar records — a name link would duplicate the default language's
 * destination in the same row.
 *
 * Rows are indented under the trigger (ui-react: 32px), and the active one carries a
 * visible check as well as `aria-current`, so "where you are" is not styling alone.
 */
function SidebarCountryRow({ option }: { option: LocaleOption }) {
    const [only] = option.languages

    if (option.languages.length === 1) {
        return (
            <SidebarMenuButton asChild>
                <a
                    href={only.href}
                    hrefLang={only.locale}
                    aria-current={only.isActive ? 'true' : undefined}
                    className={cn('pl-8', only.isActive && 'font-semibold')}
                >
                    <CountryFlag option={option} />
                    {option.countryName}
                    {only.isActive && (
                        <CheckIcon
                            data-slot="active-country-check"
                            className="ml-auto"
                            aria-hidden="true"
                        />
                    )}
                </a>
            </SidebarMenuButton>
        )
    }

    return (
        <div className="text-sidebar-foreground flex items-center gap-2 py-2 pr-2 pl-8 text-sm">
            <CountryFlag option={option} />
            <span className={cn(option.isActive && 'font-semibold')}>{option.countryName}</span>
            {option.languages.map((language) => (
                <a
                    key={language.locale}
                    href={language.href}
                    hrefLang={language.locale}
                    // `EN` is legible in a chip and meaningless read aloud, so the
                    // accessible name spells out the country and the language.
                    aria-label={`${option.countryName} — ${language.languageName}`}
                    aria-current={language.isActive ? 'true' : undefined}
                    className={cn(
                        'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded px-1.5 py-0.5 text-xs tracking-wide',
                        language.isActive && 'text-sidebar-foreground font-semibold',
                    )}
                >
                    {language.label}
                    {language.isActive && (
                        <CheckIcon
                            data-slot="active-country-check"
                            className="ml-1 inline size-3"
                            aria-hidden="true"
                        />
                    )}
                </a>
            ))}
        </div>
    )
}

export const AppSidebar = ({
    menu,
    auth,
    localeOptions,
    currencyOptions,
    copyrightYear,
    ...props
}: Omit<NavbarProps, 'logo'> & {
    /**
     * Resolved on the server. Reading the clock here instead would be evaluated once
     * into the served HTML and again on hydration, and the two disagree across New Year
     * (`invariants.md#ssr-safe-rendered-dates`).
     */
    copyrightYear: string
} & React.ComponentProps<typeof Sidebar>) => {
    const t = useTranslations('navigation')
    const tCurrency = useTranslations('currency')
    const locale = useLocale()
    const { session, logout } = useSession()
    const isLoggedIn = session.isAuthenticated
    const fullName = [session.firstName, session.lastName].filter(Boolean).join(' ')
    // The picker needs both a choice to offer and a current country to sit on the
    // trigger row; `LocaleMenu` gates the navbar copy identically. Resolved once so
    // the JSX below reads as "render it or don't".
    const active = localeOptions && activeLocaleOption(localeOptions)
    const currencyActive = currencyOptions?.find((option) => option.isActive)
    const currencyPicker =
        currencyOptions && currencyOptions.length > 1 && currencyActive
            ? { options: currencyOptions, active: currencyActive }
            : undefined
    const localePicker =
        localeOptions && localeOptions.length > 1 && active
            ? { options: localeOptions, active }
            : undefined

    return (
        <Sidebar collapsible="offcanvas" side="right" {...props}>
            {auth && (
                <SidebarHeader>
                    {/* Account control — mirrors the desktop navbar: logged-out offers
                        sign-up + log-in; logged-in greets by name. Gated on useSession;
                        the offcanvas panel is hidden until opened (post-hydration), so the
                        SSR-logged-out state keeps the HTML user-independent without a flash. */}
                    {isLoggedIn ? (
                        <div className="px-2 py-3 text-base font-semibold">
                            {fullName ? t('greeting', { name: fullName }) : t('myAccount')}
                        </div>
                    ) : (
                        <div className="space-y-2.5 p-2">
                            <Button
                                asChild
                                variant="strong"
                                size="lg"
                                className="w-full font-bold tracking-[2px] uppercase"
                                aria-label={t('signUpAriaLabel')}
                            >
                                <Link href={auth.signup.url} native={auth.signup.native}>
                                    {auth.signup.title}
                                </Link>
                            </Button>
                            <Button
                                asChild
                                variant="strong-outline"
                                size="lg"
                                className="w-full font-bold tracking-[2px] uppercase"
                                aria-label={t('logInAriaLabel')}
                            >
                                <Link
                                    href={auth.login.url}
                                    native={auth.login.native}
                                    onClick={() =>
                                        trackNavClickEvent({
                                            label: 'log_in',
                                            navType: 'hamburger',
                                        })
                                    }
                                >
                                    {auth.login.title}
                                </Link>
                            </Button>
                        </div>
                    )}
                </SidebarHeader>
            )}

            <SidebarContent className="gap-0">
                {auth && (
                    <>
                        {/* Account links — rendered in both states (ui-react
                            AccountMenuContent parity); Log Out is logged-in only. */}
                        <SidebarGroup>
                            <SidebarGroupContent>
                                <SidebarMenu>
                                    <SidebarMenuItem>
                                        <SidebarMenuButton asChild>
                                            <Link
                                                href={auth.myTrips.url}
                                                native={auth.myTrips.native}
                                            >
                                                {auth.myTrips.title}
                                            </Link>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                    <SidebarMenuItem>
                                        <SidebarMenuButton asChild>
                                            <Link
                                                href={auth.account.url}
                                                native={auth.account.native}
                                            >
                                                {auth.account.title}
                                            </Link>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                    <SidebarMenuItem>
                                        <SidebarMenuButton asChild>
                                            <Link
                                                href={auth.points.url}
                                                native={auth.points.native}
                                            >
                                                {auth.points.title}
                                            </Link>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                    {isLoggedIn && (
                                        <SidebarMenuItem>
                                            <SidebarMenuButton
                                                onClick={() => {
                                                    trackNavClickEvent({
                                                        label: 'log_out',
                                                        navType: 'hamburger',
                                                    })
                                                    void logout()
                                                }}
                                            >
                                                {t('logOut')}
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                    )}
                                </SidebarMenu>
                            </SidebarGroupContent>
                        </SidebarGroup>

                        <div className="px-2">
                            <SidebarSeparator className="mx-auto" />
                        </div>
                    </>
                )}

                {menu.map((item) =>
                    item.items && item.items.length > 0 ? (
                        <Collapsible key={item.title} className="group/collapsible">
                            <SidebarGroup>
                                <SidebarGroupLabel
                                    asChild
                                    className="group/label text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground text-sm"
                                >
                                    <CollapsibleTrigger>
                                        {item.title}
                                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                                    </CollapsibleTrigger>
                                </SidebarGroupLabel>
                                <CollapsibleContent className="animate-collapsible-down overflow-hidden data-[state=closed]:animate-collapsible-up">
                                    <SidebarGroupContent>
                                        <SidebarMenu>
                                            {item.items.map((subItem) => (
                                                <SidebarMenuItem key={subItem.title}>
                                                    <SidebarMenuButton asChild>
                                                        <Link
                                                            href={subItem.url}
                                                            native={subItem.native}
                                                        >
                                                            {subItem.title}
                                                        </Link>
                                                    </SidebarMenuButton>
                                                </SidebarMenuItem>
                                            ))}
                                        </SidebarMenu>
                                    </SidebarGroupContent>
                                </CollapsibleContent>
                            </SidebarGroup>
                        </Collapsible>
                    ) : (
                        <SidebarGroup key={item.title}>
                            <SidebarGroupContent>
                                <SidebarMenu>
                                    <SidebarMenuItem>
                                        <SidebarMenuButton asChild>
                                            <Link href={item.url} native={item.native}>
                                                {item.title}
                                            </Link>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                </SidebarMenu>
                            </SidebarGroupContent>
                        </SidebarGroup>
                    ),
                )}

                {/* Country picker — after the site menu, ui-react's offcanvas order.
                    A collapsible group, mirroring ui-react's `VerticalLocaleMenu`: the
                    trigger row IS the active country (flag + name), so a visitor sees
                    where they are without opening anything, and the list stays out of
                    the way of the menu above it. Same primitive as the menu groups
                    beside it — the crawlable copy of these links is the navbar's
                    `<details>`, which ships in the markup at every viewport, so this
                    one is free to mount its content on open like its neighbours. */}
                {localePicker && (
                    <>
                        <div className="px-2">
                            <SidebarSeparator className="mx-auto" />
                        </div>
                        <Collapsible className="group/locale">
                            <SidebarGroup>
                                <SidebarGroupLabel
                                    asChild
                                    // `gap-2` matches SidebarMenuButton's own flag-to-name
                                    // spacing, which SidebarGroupLabel does not set — without
                                    // it the trigger row reads "🇲🇽México" while the rows
                                    // below it are spaced.
                                    className="group/label text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground h-auto gap-2 py-2 text-sm"
                                >
                                    <CollapsibleTrigger
                                        // Purpose AND current country in one name: an
                                        // aria-label replaces name-from-content, so the
                                        // visible country name would otherwise be the
                                        // only thing announced.
                                        aria-label={`${t('chooseCountry')} — ${localePicker.active.countryName}`}
                                    >
                                        <CountryFlag option={localePicker.active} />
                                        {localePicker.active.countryName}
                                        {/* A plus that rotates into an ✕ when open —
                                            ui-react's own affordance for this row
                                            (`listToggleActive`: rotate(45deg)). */}
                                        <PlusIcon
                                            className="ml-auto transition-transform duration-200 group-data-[state=open]/locale:rotate-45"
                                            aria-hidden="true"
                                        />
                                    </CollapsibleTrigger>
                                </SidebarGroupLabel>
                                <CollapsibleContent className="animate-collapsible-down overflow-hidden data-[state=closed]:animate-collapsible-up">
                                    <SidebarGroupContent>
                                        <SidebarMenu>
                                            {localePicker.options.map((option) => (
                                                <SidebarMenuItem key={option.countryCode}>
                                                    <SidebarCountryRow option={option} />
                                                </SidebarMenuItem>
                                            ))}
                                        </SidebarMenu>
                                    </SidebarGroupContent>
                                </CollapsibleContent>
                            </SidebarGroup>
                        </Collapsible>
                    </>
                )}

                {/* Currency picker — the vertical mirror of the navbar's, which is
                    `lg:`-only, so below that breakpoint this is the only way to choose
                    one. Same collapsible shape as the country picker above it; the
                    trigger row is the selected currency, so a visitor sees it without
                    opening anything. */}
                {currencyPicker && (
                    <>
                        <div className="px-2">
                            <SidebarSeparator className="mx-auto" />
                        </div>
                        <Collapsible className="group/currency">
                            <SidebarGroup>
                                <SidebarGroupLabel
                                    asChild
                                    className="group/label text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground h-auto gap-2 py-2 text-sm"
                                >
                                    <CollapsibleTrigger
                                        aria-label={`${tCurrency('menuLabel')} — ${tCurrency(currencyPicker.active.currency.toLowerCase())}`}
                                    >
                                        <span className="font-bold" aria-hidden="true">
                                            {currencySymbol(locale, currencyPicker.active.currency)}
                                        </span>
                                        {tCurrency(currencyPicker.active.currency.toLowerCase())}
                                        <PlusIcon
                                            className="ml-auto transition-transform duration-200 group-data-[state=open]/currency:rotate-45"
                                            aria-hidden="true"
                                        />
                                    </CollapsibleTrigger>
                                </SidebarGroupLabel>
                                <CollapsibleContent className="animate-collapsible-down overflow-hidden data-[state=closed]:animate-collapsible-up">
                                    <SidebarGroupContent>
                                        <CurrencyMenuVertical
                                            options={currencyPicker.options}
                                            locale={locale}
                                        />
                                    </SidebarGroupContent>
                                </CollapsibleContent>
                            </SidebarGroup>
                        </Collapsible>
                    </>
                )}
            </SidebarContent>

            <SidebarFooter>
                <div className="text-sidebar-foreground/70 p-2 text-center text-xs">
                    © {copyrightYear} Wanderu
                </div>
            </SidebarFooter>

            <SidebarRail aria-label={t('toggleSidebar')} title={t('toggleSidebar')} />
        </Sidebar>
    )
}
